import { useMemo } from 'react';

import { TextOverlayLockedFeatures } from 'blocks/TextOverlayModal/v2';

import {
  getLockedFeature,
  KEY_TEXT_OVERLAY_SLOTS,
  KeyTextOverlayConfig,
  useEditorState,
} from '../../context/VideoEditorStateContext';
import { useTemplateState } from '../../context/VideoTemplateStateContext';
import { VideoTemplateStateContent } from '../../state/template/types';

/**
 * Inverse lookup: which named slot, if any, points at this overlay id?
 * The state stores slot→id pointers at the top level (e.g.
 * `state.podcastTitle.id`); the overlay record itself does not carry the
 * textType.
 */
function findSlotForOverlay(
  state: VideoTemplateStateContent,
  overlayId: string,
): keyof KeyTextOverlayConfig | undefined {
  return KEY_TEXT_OVERLAY_SLOTS.find(slot => {
    const asset = state[slot];
    return asset?.type === 'textOverlay' && asset.id === overlayId;
  });
}

/**
 * Resolves the {@link TextOverlayLockedFeatures} for the named-slot text
 * overlay identified by `assetId`. Returns `undefined` when no lock applies
 * (free-form overlay, unknown slot, or no relevant lockSpec entry).
 */
export default function useTextOverlayLockedFeatures(
  assetId: string,
): TextOverlayLockedFeatures | undefined {
  const { features } = useEditorState();
  const templateState = useTemplateState();
  const slot = findSlotForOverlay(templateState, assetId);

  return useMemo(() => {
    if (!slot) {
      return undefined;
    }

    const fontFamily = getLockedFeature(features, [
      'textOverlay',
      slot,
      'style',
      'fontFamily',
    ]);
    if (!fontFamily) {
      return undefined;
    }

    return { fontFamily };
  }, [features, slot]);
}
